import * as React from 'react';
import Stardog, { query as StardogQuery } from 'stardog';
import { v4 as uuid } from 'uuid';

import { MimeType } from 'bones/constants/mimeTypes';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxConversationContextValue } from 'bones/constants/voicebox/conversation';
import {
  VoiceboxApp,
  VoiceboxMessage,
  VoiceboxRequestMessage,
  VoiceboxResponse,
  VoiceboxResponseAction,
  VoiceboxResponseActionType,
  VoiceboxResponseMessage,
  VoiceboxResponseMessageType,
} from 'bones/constants/voicebox/message';
import { VoiceboxSchema } from 'bones/constants/voicebox/schema';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { exportToFile } from 'bones/utils/exportToFile';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { usePrevious } from 'bones/utils/hooks/usePrevious';
import { isEqual } from 'bones/utils/isEqual';
import { runWithRefetchConnectionToken } from 'bones/utils/portal/refetchConnectionToken';
import { useStardogConnectionFromPortalIndex } from 'bones/utils/portal/useStardogConnectionFromPortalIndex';
import {
  requestStardogVoiceboxFeedback,
  requestStardogVoiceboxMessage,
} from 'bones/utils/voicebox/request';
import { StardogVoiceboxMessageResponse } from 'bones/utils/voicebox/types';
import { usePortalVoiceboxConversation } from 'bones/utils/voicebox/usePortalVoiceboxConversation';

export interface PortalVoiceboxProps {
  /**
   * the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  id?: string | null;
  /**
   * a unique key for manually triggering a state refresh
   */
  key?: string | null;
  initialConversationId?: string;
  schema: VoiceboxSchema;
  staticMessages?: VoiceboxMessage[];

  /**
   * @param id the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  onDownloadCsvSuccess: (id: string | null, filename: string) => void;
  /**
   * @param err: the error message
   * @param id the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  onDownloadCsvError: (err: string, id: string | null) => void;
  /**
   * @param err: the error message
   * @param id the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  onFeedbackError: (err: string, id: string | null) => void;
  /**
   * @param err: the error message
   * @param id the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  onMessageError: (err: string, id: string | null) => void;
  /**
   * @param message: the voicebox response message
   * @param id the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  onMessageSuccess: (
    message: VoiceboxResponseMessage,
    id: string | null
  ) => void;
}

export const usePortalVoicebox = ({
  id = null,
  initialConversationId,
  key = null,
  schema,
  staticMessages = [],
  onDownloadCsvSuccess,
  onDownloadCsvError,
  onFeedbackError,
  onMessageError,
  onMessageSuccess,
}: PortalVoiceboxProps): VoiceboxConversationContextValue => {
  const isMounted = useIsMounted();
  const didIdChange = useDidValueChange(id);
  const didKeyChange = useDidValueChange(key);

  const stardogConnection = useStardogConnectionFromPortalIndex(
    schema.config.connectionIdx
  );

  const didInitialConversationIdChange = useDidValueChange(
    initialConversationId
  );

  const prevConnectionId = usePrevious(schema.config.connectionId);
  const didConnectionIdChange = useDidValueChange(schema.config.connectionId);

  const [isLoadingFeedback, setIsLoadingFeedback] = React.useState(false);
  const pendingFeedbackIds = React.useRef(new Set<string>());
  const [isLoadingMessage, setIsLoadingMessage] = React.useState(false);
  const pendingMessageId = React.useRef('');
  const [loadingDownloadMessageId, setLoadingDownloadMessageId] =
    React.useState('');
  const pendingDownloadId = React.useRef('');

  const [conversationId, setConversationId] = React.useState(
    () => initialConversationId || ''
  );
  const [isLoadingInitialConversation, setIsLoadingInitialConversation] =
    React.useState(false);
  const pendingInitialConverstionId = React.useRef<string | undefined>('');
  const [messages, setMessages] = React.useState<VoiceboxMessage[]>([]);

  const conversation = usePortalVoiceboxConversation(conversationId);
  const didConversationChange = useDidValueChange(
    {
      data: conversation.data,
      isLoading: conversation.isLoading,
      isRefetching: conversation.isRefetching,
    },
    isEqual
  );

  // reload the conversation when the id, connection, or conversation id changes
  React.useEffect(() => {
    if (
      didIdChange ||
      didKeyChange ||
      (prevConnectionId !== undefined && didConnectionIdChange) ||
      (didInitialConversationIdChange &&
        initialConversationId &&
        initialConversationId !== conversationId)
    ) {
      setIsLoadingFeedback(false);
      setIsLoadingMessage(false);
      setLoadingDownloadMessageId('');
      setIsLoadingInitialConversation(Boolean(initialConversationId));
      pendingInitialConverstionId.current = initialConversationId;
      setMessages([]);
      conversation.remove();
      setConversationId(initialConversationId || '');
    }
  }, [
    conversation,
    conversationId,
    didConnectionIdChange,
    didIdChange,
    didInitialConversationIdChange,
    didKeyChange,
    initialConversationId,
    prevConnectionId,
  ]);

  // reset messages when loaded from a conversation id change
  React.useEffect(() => {
    if (
      didConversationChange &&
      !conversation.isLoading &&
      !conversation.isRefetching
    ) {
      if (!conversation.data) {
        setIsLoadingInitialConversation(false);
        return;
      }

      if (conversation.data.conversationId === conversationId) {
        const nextMessages = conversation.data.messages;
        if (nextMessages) {
          setMessages((prev) =>
            isEqual(prev, nextMessages) ? prev : nextMessages
          );
        } else {
          setConversationId('');
          setMessages([]);
        }
      }

      if (
        conversation.data.conversationId === pendingInitialConverstionId.current
      ) {
        setIsLoadingInitialConversation(false);
      }
    }
  }, [conversation, conversationId, didConversationChange]);

  const idRef = React.useRef(id);
  React.useEffect(() => {
    idRef.current = id;
  }, [id]);

  const handleDownloadCsv = React.useCallback(
    async (
      message: VoiceboxResponseMessage,
      maybeSettings?: VoiceboxSettings
    ): Promise<boolean> => {
      let succeeded: boolean;

      const newPendingId = uuid();
      pendingDownloadId.current = newPendingId;
      setLoadingDownloadMessageId(message.messageId);

      const downloadSparqlAction =
        message.actions?.find(
          ({ type }) => type === VoiceboxResponseActionType.DOWNLOAD_SPARQL
        ) ||
        message.actions?.find(
          ({ type }) => type === VoiceboxResponseActionType.SPARQL
        );

      if (!downloadSparqlAction || !stardogConnection) {
        return false;
      }
      const settings =
        maybeSettings ||
        schema.config.initialSettings ||
        schema.config.defaultSettings;

      const params: Record<string, any> = {
        reasoning: settings.reasoning,
        'graph-uri': settings.namedGraphs,
      };
      if (settings.reasoning) {
        params.schema = settings.model || DEFAULT_MODEL_NAME;
      }

      try {
        const response = await runWithRefetchConnectionToken(
          stardogConnection,
          schema.config.connectionIdx,
          (stardogConn) =>
            StardogQuery.execute(
              stardogConn,
              settings.databaseId,
              downloadSparqlAction.value,
              MimeType.CSV as Stardog.HTTP.AcceptMimeType,
              params
            )
        );

        if (!response.ok) {
          throw new Error(response.statusText || String(response.status));
        }

        const filename = 'voicebox_response.csv';
        if (response.body) {
          exportToFile({
            contents: response.body,
            filename,
            mimeType: MimeType.CSV,
          });
        }
        onDownloadCsvSuccess(id, filename);
        succeeded = true;
      } catch (err) {
        const errMsg =
          (err as Error).message || schema.copy.errors.unknownError;
        onDownloadCsvError(errMsg, id);
        succeeded = false;
      }

      if (
        !isMounted.current ||
        id !== idRef.current ||
        pendingDownloadId.current !== newPendingId
      ) {
        return succeeded;
      }

      if (pendingDownloadId.current === newPendingId) {
        setLoadingDownloadMessageId('');
      }

      return succeeded;
    },
    [
      id,
      isMounted,
      onDownloadCsvError,
      onDownloadCsvSuccess,
      schema,
      stardogConnection,
    ]
  );

  const handleSendFeedback = React.useCallback(
    async (
      message: VoiceboxResponseMessage,
      feedbackScore?: number,
      feedbackComment?: string
    ): Promise<VoiceboxResponse> => {
      const newPendingId = uuid();
      pendingFeedbackIds.current.add(newPendingId);
      setIsLoadingFeedback(true);

      let succeeded = false;
      let newMessage: VoiceboxResponse['newMessage'];
      try {
        const responseMessage = await requestStardogVoiceboxFeedback({
          comment: feedbackComment,
          messageId: message.messageId,
          score: feedbackScore,
        });
        newMessage = { ...message, feedbackScore };

        if (!isMounted.current || id !== idRef.current) {
          return { id, newMessage, succeeded: true };
        }

        if (responseMessage.error === undefined) {
          setMessages((prevMessages) =>
            prevMessages.map((prevMessage) =>
              prevMessage.messageId !== message.messageId
                ? prevMessage
                : { ...prevMessage, feedbackScore }
            )
          );
          succeeded = true;
        } else {
          throw new Error(responseMessage.error);
        }
      } catch (err) {
        const errMsg =
          (err as Error).message || schema.copy.errors.unknownError;
        onFeedbackError(errMsg, id);
      }

      pendingFeedbackIds.current.delete(newPendingId);
      if (!pendingFeedbackIds.current.size) {
        setIsLoadingFeedback(false);
      }

      return { id, newMessage, succeeded };
    },
    [id, isMounted, onFeedbackError, schema]
  );

  const handleSendMessage = React.useCallback(
    async (
      query: string,
      maybeSettings?: VoiceboxSettings,
      aiReasoning?: boolean
    ): Promise<VoiceboxResponse> => {
      const newPendingId = uuid();
      pendingMessageId.current = newPendingId;
      setIsLoadingMessage(true);

      const settings =
        maybeSettings ||
        schema.config.initialSettings ||
        schema.config.defaultSettings;
      const requestMessage: VoiceboxRequestMessage = {
        app: schema.config.app,
        connectionId: schema.config.connectionId,
        context:
          schema.config.app === VoiceboxApp.DESIGNER
            ? {
                ...schema.config.context,
              }
            : {
                ...schema.config.context,
                database_id: settings.databaseId,
                model: settings.model,
                named_graphs: settings.namedGraphs,
                reasoning: settings.reasoning,
              },
        conversationId,
        messageId: '',
        query,
        isRequest: true,
      };
      setMessages((prevMessages) => [...prevMessages, requestMessage]);

      let succeeded = false;
      let newMessage: VoiceboxResponse['newMessage'];

      const handleMessageFailure = (err: string) => {
        succeeded = false;
        const errMsg = err || schema.copy.errors.unknownError;
        newMessage = {
          conversationId,
          messageId: '',
          result: schema.copy.errors.failureWithError(query, errMsg),
          success: false,
          type: VoiceboxResponseMessageType.FAILURE,
          isResponse: true,
        };

        if (
          !isMounted.current ||
          id !== idRef.current ||
          pendingMessageId.current !== newPendingId
        ) {
          return;
        }

        onMessageError(errMsg, id);
        setMessages((prevMessages) => [
          ...prevMessages,
          newMessage as VoiceboxMessage,
        ]);
      };

      const handleMessageSuccess = (
        responseMessage: StardogVoiceboxMessageResponse
      ) => {
        succeeded = true;
        newMessage = {
          actions: responseMessage.actions as VoiceboxResponseAction[],
          conversationId: responseMessage.conversationId,
          examples: responseMessage.examples,
          messageId: responseMessage.messageId,
          result: responseMessage.result,
          pending: responseMessage.pending,
          success: responseMessage.success,
          type: responseMessage.type as VoiceboxResponseMessageType | undefined,
          isResponse: true,
        };

        if (
          !isMounted.current ||
          id !== idRef.current ||
          pendingMessageId.current !== newPendingId
        ) {
          return;
        }

        setConversationId(newMessage.conversationId);
        if (!responseMessage.pending && responseMessage.type) {
          // if not an intermediate reasoning message
          onMessageSuccess(newMessage, id);
          conversation.refetch();
          return;
        }

        setMessages((prevMessages) => [
          ...prevMessages.filter(
            (message) =>
              message.messageId !== (newMessage as VoiceboxMessage).messageId
          ),
          newMessage as VoiceboxMessage,
        ]);
      };

      const responseMessage = await requestStardogVoiceboxMessage(
        aiReasoning ? 'v3' : 'v2',
        requestMessage
      );

      if (responseMessage.error !== undefined) {
        handleMessageFailure(responseMessage.error);
      } else {
        let newMessage: StardogVoiceboxMessageResponse | null;
        do {
          // eslint-disable-next-line no-await-in-loop
          newMessage = await responseMessage.next();
          if (newMessage) {
            handleMessageSuccess(newMessage);
          }
        } while (newMessage);
      }

      if (
        isMounted.current &&
        id === idRef.current &&
        pendingMessageId.current === newPendingId
      ) {
        setIsLoadingMessage(false);
      }

      return { id, newMessage, succeeded };
    },
    [
      conversation,
      conversationId,
      id,
      isMounted,
      onMessageError,
      onMessageSuccess,
      schema,
    ]
  );

  const allMessages = React.useMemo(
    () => staticMessages.concat(messages),
    [messages, staticMessages]
  );

  return {
    conversationId,
    id,
    isLoadingConversation:
      isLoadingInitialConversation || conversation.isLoading,
    isLoadingInitialConversation,
    isLoadingFeedback,
    isLoadingMessage,
    isRefetchingConversation: conversation.isRefetching,
    loadingDownloadMessageId,
    messages: allMessages,
    downloadCsv: handleDownloadCsv,
    sendFeedback: handleSendFeedback,
    sendMessage: handleSendMessage,
  };
};
